import type { TimelineScrubberBucket } from '@/components/timeline-scrubber';
import type { DssOi, OiSummary } from '@/lib/api/types';
import { filterExternalOis } from '@/lib/oi/externalFilter';
import { overlapsHour } from '@/lib/oi/oiTime';
import type { OiCategory, OiStatusFilter } from '@/lib/store/oi';
import Fuse, { type IFuseOptions } from 'fuse.js';

export const SCRUBBER_HOURS = 24;

export type OiEmptyState = 'no-active' | 'no-match' | null;

/** ENG-188 copy, verbatim. */
export const EMPTY_COPY: Record<Exclude<OiEmptyState, null>, string> = {
  'no-active': 'You do not have any active OIs',
  'no-match': 'No OIs match your search criteria',
};

export type FilteredOis = {
  own: OiSummary[];
  partnerTenants: [string, OiSummary[]][];
  external: DssOi[];
  emptyState: OiEmptyState;
};

export type FilterOisOptions = {
  category: OiCategory;
  statusFilters: Record<OiStatusFilter, boolean>;
  query: string;
  selectedHour: number | null;
  now: number;
};

// Weighted toward what an operator types first — ids and callsigns (same
// shape as inv/droneSearch.ts).
const SUMMARY_KEYS: IFuseOptions<OiSummary>['keys'] = [
  { name: 'oi_id', weight: 2 },
  { name: 'aircraft', weight: 2 },
  { name: 'title', weight: 1 },
  { name: 'partner_tenant', weight: 1 },
  { name: 'op_type', weight: 0.5 },
];

function fuzzy<T>(items: T[], keys: IFuseOptions<T>['keys'], query: string): T[] {
  const term = query.trim();
  if (!term) return items;
  return new Fuse(items, { keys, threshold: 0.35, ignoreLocation: true })
    .search(term)
    .map((r) => r.item);
}

/** Category → status chips (own + partner) → selected hour (external only,
 *  spec D7) → fuzzy search. "All" is Internal + Partner (spec D3). */
export function filterOis(
  input: { list: OiSummary[]; external: DssOi[] },
  o: FilterOisOptions,
): FilteredOis {
  const showInternal = o.category === 'all' || o.category === 'internal';
  const showPartner = o.category === 'all' || o.category === 'partner';
  // All lists the same 3rd Party results the 3rd Party tab shows (empty until
  // a search area exists).
  const showExternal = o.category === 'all' || o.category === '3rdparty';

  const byStatus = input.list.filter((oi) => o.statusFilters[oi.status as OiStatusFilter]);
  const own = showInternal
    ? fuzzy(
        byStatus.filter((oi) => (oi.origin ?? 'internal') !== 'partner'),
        SUMMARY_KEYS,
        o.query,
      )
    : [];
  const partners = showPartner
    ? fuzzy(
        byStatus.filter((oi) => oi.origin === 'partner'),
        SUMMARY_KEYS,
        o.query,
      )
    : [];
  // Tenants alphabetical so the list doesn't reorder as OI updates arrive.
  const partnerTenants = Object.entries(
    partners.reduce<Record<string, OiSummary[]>>((acc, oi) => {
      const key = oi.partner_tenant ?? 'Unknown tenant';
      const bucket = acc[key] ?? [];
      bucket.push(oi);
      acc[key] = bucket;
      return acc;
    }, {}),
  ).sort(([a], [b]) => a.localeCompare(b));

  const external = showExternal
    ? filterExternalOis(input.external, { hour: o.selectedHour, query: o.query, now: o.now })
    : [];

  const total = own.length + partners.length + external.length;
  // spec §4.4 — "no active OIs" only when the tenant has no internal OIs at
  // all; anything hidden by chips or the query is a "no match".
  const hasInternal = input.list.some((oi) => (oi.origin ?? 'internal') !== 'partner');
  const emptyState: OiEmptyState =
    total > 0
      ? null
      : o.category === 'internal' && o.query.trim() === '' && !hasInternal
        ? 'no-active'
        : 'no-match';
  return { own, partnerTenants, external, emptyState };
}

/** Scrubber density — every external OI the DSS returned for the area,
 *  regardless of the selected hour or the text search. */
export function buildHourBuckets(external: DssOi[], now: number): TimelineScrubberBucket[] {
  return Array.from({ length: SCRUBBER_HOURS }, (_, offsetHours) => ({
    offsetHours,
    count: external.filter((oi) => overlapsHour(oi.start_utc, oi.end_utc, offsetHours, now)).length,
  }));
}
